import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '../i18n/useLanguage';

// Delay before the automatic bounce back to `/`, long enough for the
// visitor to read the message but short enough not to feel stuck.
const REDIRECT_DELAY_MS = 3000;

/**
 * 404 page. Reuses the UnderConstruction scene (cream `bg-tan` backdrop,
 * hero pattern, bottom fade, floating side-characters, logo) so a wrong
 * URL still feels on-brand, then auto-redirects home after a short beat.
 * A manual "back home" link is always present for users who'd rather not
 * wait and for crawlers/keyboard users (the redirect is JS-only).
 *
 * Rendered by the catch-all `*` route in main.tsx. On the deployed site
 * CloudFront's SPA fallback (404 → /index.html) is what lets any unknown
 * path reach React Router in the first place.
 */
export default function NotFound() {
  const t = useT();
  const navigate = useNavigate();

  useEffect(() => {
    const id = window.setTimeout(
      () => navigate('/', { replace: true }),
      REDIRECT_DELAY_MS,
    );
    return () => window.clearTimeout(id);
  }, [navigate]);

  return (
    <main className="relative isolate flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-tan px-4 py-10">
      {/* Background pattern overlay — same asset HeroSection /
          UnderConstruction use, behind everything. */}
      <img
        src="/images/hero-bg-pattern.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover"
      />

      {/* Bottom white fade — mirrors UnderConstruction. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-[4] h-[40%] bg-gradient-to-t from-white via-white/40 to-transparent"
      />

      {/* Floating side-character art (out of phase, as on the landing). */}
      <img
        src="/images/hero-character-right.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-[-28%] lg:left-auto right-auto lg:right-0 top-[16%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[95%] object-contain select-none animate-float-y"
        draggable={false}
      />
      <img
        src="/images/hero-character-left.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[-20%] lg:right-auto left-auto lg:left-0 top-[8%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[120%] object-contain select-none animate-float-y [animation-delay:-2s]"
        draggable={false}
      />

      {/* Foreground column — logo, "404 / not found" pill, redirect
          notice, and a manual back-home link. */}
      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center gap-6 px-4 md:gap-8 md:px-8 lg:gap-10">
        <img
          src="/images/logo.png"
          alt={t('hero.title')}
          className="h-28 w-auto select-none md:h-32 lg:h-[160px]"
          draggable={false}
        />

        {/* Title pill — same cream lozenge as UnderConstruction, with
            a big "404" and the localized not-found message. */}
        <div className="flex shrink-0 flex-col items-center justify-center gap-[10px] rounded-[100px] bg-[#F6EADF] px-6 py-2 md:px-10 md:py-4 lg:px-14 lg:py-5">
          <div className="flex w-full items-center justify-center gap-4 px-4 md:gap-4 lg:gap-5">
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 bg-section-feature md:h-6 md:w-6 lg:h-8 lg:w-8"
              style={{
                WebkitMaskImage: "url('/images/fork-spoon.svg')",
                maskImage: "url('/images/fork-spoon.svg')",
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
            <h1 className="hero-title-shadow mt-0 mb-1 text-center font-display text-2xl font-extrabold leading-none text-white md:mb-3 md:text-4xl lg:text-[48px]">
              404
            </h1>
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 bg-section-feature md:h-6 md:w-6 lg:h-8 lg:w-8"
              style={{
                WebkitMaskImage: "url('/images/fork-spoon.svg')",
                maskImage: "url('/images/fork-spoon.svg')",
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
          </div>
        </div>

        {/* Not-found message + auto-redirect notice. `aria-live` so a
            screen reader announces the redirect is happening. */}
        <div
          aria-live="polite"
          className="flex flex-col items-center gap-1 text-center text-section-feature"
        >
          <p className="text-lg font-bold md:text-xl">{t('notFound.title')}</p>
          <p className="text-sm opacity-80 md:text-base">
            {t('notFound.redirecting')}
          </p>
        </div>

        {/* Manual fallback — works without JS (the auto-redirect is
            JS-only) and gives impatient users an immediate way home. */}
        <Link
          to="/"
          className="text-sm font-bold text-section-feature underline underline-offset-2 transition hover:opacity-80 md:text-base"
        >
          {t('legal.backHome')}
        </Link>
      </div>
    </main>
  );
}
